/**
 * NCC: fade and wipe transitions for Horizon slideshows (sections/slideshow.liquid setting
 * "Transition" -> `data-transition="fade|wipe"` on .slideshow-section).
 *
 * Horizon moves between slides by smooth-scrolling a scroll-snap container. In these modes we:
 *  1. make every programmatic selection (autoplay, dots, arrows) an instant jump,
 *  2. clone the outgoing slide (image, overlay, text) into a backdrop behind the scroller,
 *  3. run a CSS transition on the incoming slide (and, for wipe, on the backdrop):
 *     - fade: incoming slide opacity 0 -> 1 (1.2s)
 *     - wipe: incoming slide clip edge sweeps in from the right while its media/content and the
 *       backdrop drift at half speed (1s, Lager-style parallax reveal), heading revealed by a mask.
 * Touch swipes still scroll natively and are left alone. Reduced-motion users get a plain cut.
 * CSS lives in sections/slideshow.liquid.
 */
import { SlideshowSelectEvent } from '@theme/events';

const IN = 'ncc-transition-in';
const FROM = 'ncc-transition-from';
const BACKDROP = 'ncc-slide-backdrop';
const CLEANUP_MS = 2400; // longest transition (heading mask, 2s) plus a margin

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {Element} section */
function init(section) {
  const mode = section.getAttribute('data-transition');
  if (mode !== 'fade' && mode !== 'wipe') return;

  const component = section.querySelector('slideshow-component');
  const container = section.querySelector('slideshow-container');
  if (!component || !container || component.dataset.nccTransition) return;
  component.dataset.nccTransition = mode;

  // Instance-level override so next()/previous()/dot clicks (which call this.select) jump instantly.
  const select = component.select.bind(component);
  component.select = (input, event, options = {}) => select(input, event, { ...options, animate: false });

  /** @type {HTMLElement | null} */
  let backdrop = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let cleanup;

  const clear = () => {
    clearTimeout(cleanup);
    backdrop?.remove();
    backdrop = null;
    for (const slide of component.querySelectorAll(`.${IN}, .${FROM}`)) slide.classList.remove(IN, FROM);
  };

  component.addEventListener(SlideshowSelectEvent.eventName, (event) => {
    const { slide: next, index, previousIndex, trigger } = event.detail ?? {};
    // Horizon also reports the landed slide with trigger "scroll"; only programmatic selects animate.
    if (!(next instanceof HTMLElement) || trigger !== 'select' || index === previousIndex) return;
    if (prefersReducedMotion()) return;

    clear();

    const previous = component.slides?.[previousIndex];
    if (previous instanceof HTMLElement) {
      backdrop = document.createElement('div');
      backdrop.className = BACKDROP;
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.innerHTML = previous.innerHTML;
      for (const el of backdrop.querySelectorAll('[id]')) el.removeAttribute('id');
      container.prepend(backdrop);
    }

    // 1. commit the start state with no transition active (FROM only), then
    // 2. enable transitions (IN) and release the start state in the next frame, so the
    //    browser interpolates from +50% / masked rather than from the current values.
    next.classList.add(FROM);
    void next.offsetWidth;
    requestAnimationFrame(() => {
      next.classList.add(IN);
      next.classList.remove(FROM);
      backdrop?.classList.add(`${BACKDROP}--out`);
    });
    cleanup = setTimeout(clear, CLEANUP_MS);
  });
}

const initAll = () => document.querySelectorAll('.slideshow-section[data-transition]').forEach(init);

initAll();
document.addEventListener('shopify:section:load', initAll);
